import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ServerIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface NFSInstance {
  id?: string;
  name: string;
  host: string;
  port: number;
  apiKey?: string;
}

interface NFSInstanceFormProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (instance: NFSInstance) => void;
  instance?: NFSInstance;
}

const NFSInstanceForm: React.FC<NFSInstanceFormProps> = ({
  isOpen,
  onCancel,
  onSave,
  instance
}) => {
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(8080);
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (instance) {
        setName(instance.name);
        setHost(instance.host);
        setPort(instance.port || 8080);
        setApiKey(instance.apiKey || '');
      } else {
        setName('');
        setHost('');
        setPort(8080);
        setApiKey('');
      }
    }
  }, [isOpen, instance]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !host || !port) return;
    const instanceData: NFSInstance = { name, host, port, apiKey: apiKey || undefined };
    if (instance && instance.id) {
      onSave({ ...instanceData, id: instance.id });
    } else {
      onSave(instanceData);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onCancel}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-md relative z-10 max-h-[90vh] flex flex-col">
                <div className="border-b border-zinc-800 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ServerIcon className="h-5 w-5 text-indigo-500" />
                      <h2 className="text-lg font-medium text-zinc-100">
                        {instance ? 'Edit NFS Instance' : 'Add NFS Instance'}
                      </h2>
                    </div>
                    <button 
                      type="button"
                      onClick={onCancel}
                      className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col h-full">
                  <div className="p-6 space-y-6 flex-grow overflow-y-auto">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-zinc-400 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                        placeholder="e.g. Production NFS"
                      />
                    </div>
                    <div>
                      <label htmlFor="host" className="block text-sm font-medium text-zinc-400 mb-1">
                        Host
                      </label>
                      <input
                        type="text"
                        id="host"
                        required
                        value={host}
                        onChange={(e) => setHost(e.target.value)}
                        className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                        placeholder="e.g. nfs.example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="port" className="block text-sm font-medium text-zinc-400 mb-1">
                        Port
                      </label>
                      <input
                        type="number"
                        id="port"
                        required
                        min={1}
                        max={65535}
                        value={port}
                        onChange={e => setPort(Number(e.target.value))}
                        className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                        placeholder="8080"
                      />
                    </div>
                    <div>
                      <label htmlFor="apiKey" className="block text-sm font-medium text-zinc-400 mb-1">
                        API Token
                      </label>
                      <input
                        type="password"
                        id="apiKey"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                        placeholder="Optional: API token for authentication"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 px-6 py-4 flex justify-end space-x-3 bg-zinc-900 sticky bottom-0">
                    <button
                      type="button"
                      onClick={onCancel}
                      className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                    >
                      {instance ? 'Update' : 'Add'} Instance
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default NFSInstanceForm; 